import { useEffect, useState } from "react";
import { authFetch, API } from "../utils/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Users, FileText, Mail, Phone, MapPin, Calendar, Languages, Paperclip,
  ShieldCheck, Loader2, CheckCircle2,
} from "lucide-react";

const STATUS_META = {
  new:        { label: "New",        cls: "bg-sky-100 text-sky-700" },
  reviewing:  { label: "Reviewing",  cls: "bg-amber-100 text-amber-700" },
  approved:   { label: "Approved",   cls: "bg-emerald-100 text-emerald-700" },
  rejected:   { label: "Rejected",   cls: "bg-rose-100 text-rose-700" },
  archived:   { label: "Archived",   cls: "bg-slate-100 text-slate-600" },
};

export default function AdminApplicationsManager() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/admin/worker-applications`);
      if (r.ok) setApps(await r.json());
    } catch (err) {
      toast.error("Couldn't load applications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = statusFilter === "all"
    ? apps
    : apps.filter((a) => (a.status || "new") === statusFilter);

  const updateStatus = async (app, status) => {
    setSaving(true);
    try {
      const r = await authFetch(`${API}/admin/worker-applications/${app.application_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status, admin_notes: adminNotes }),
      });
      if (r.ok) {
        toast.success(`Marked as ${status}`);
        setApps((prev) => prev.map((a) => (a.application_id === app.application_id ? { ...a, status, admin_notes: adminNotes } : a)));
        setSelected(null);
      } else {
        toast.error("Couldn't update");
      }
    } catch (err) {
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-applications-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-[#87CEEB]" />
            Worker applications
          </h2>
          <p className="text-sm text-slate-500">{apps.length} total · public submissions from /worker-signup</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="applications-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(STATUS_META).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} data-testid="applications-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-200">
          <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No applications yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="applications-list">
          {filtered.map((a) => {
            const meta = STATUS_META[a.status || "new"];
            const docsCount = (a.documents || []).length;
            return (
              <button
                type="button"
                key={a.application_id}
                onClick={() => { setSelected(a); setAdminNotes(a.admin_notes || ""); }}
                className="text-left bg-white rounded-2xl border border-slate-100 hover:border-[#87CEEB] hover:shadow-sm p-5 transition-all"
                data-testid={`application-card-${a.application_id}`}
              >
                <div className="flex items-start gap-3">
                  {a.photo_data_url ? (
                    <img src={a.photo_data_url} alt="" className="w-12 h-12 rounded-xl object-cover shrink-0" />
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500 font-bold shrink-0">
                      {(a.first_name?.[0] || "?").toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <p className="font-semibold text-slate-900 truncate">{a.name}</p>
                      <Badge className={`${meta.cls} text-[10px]`}>{meta.label}</Badge>
                    </div>
                    <p className="text-xs text-slate-500 truncate">{a.email} · {a.phone}</p>
                    <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-500">
                      {a.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {a.location}</span>}
                      {a.languages && <span className="flex items-center gap-1"><Languages className="w-3 h-3" /> {a.languages.split(",").length}</span>}
                      {docsCount > 0 && <span className="flex items-center gap-1"><Paperclip className="w-3 h-3" /> {docsCount} doc{docsCount > 1 ? "s" : ""}</span>}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Detail modal */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="application-detail-modal">
          {selected && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  {selected.photo_data_url ? (
                    <img src={selected.photo_data_url} alt="" className="w-16 h-16 rounded-2xl object-cover" />
                  ) : (
                    <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-500 font-bold text-xl">
                      {(selected.first_name?.[0] || "?").toUpperCase()}
                    </div>
                  )}
                  <div>
                    <DialogTitle className="text-2xl">{selected.name}</DialogTitle>
                    <DialogDescription className="mt-1">
                      Submitted {new Date(selected.created_at).toLocaleString()} · {selected.consent_listing ? <span className="text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Consent to list given</span> : "No listing consent"}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-4 mt-2">
                <Section title="Contact">
                  <Row icon={Mail} label="Email" value={selected.email} />
                  <Row icon={Phone} label="Phone" value={selected.phone} />
                  <Row icon={MapPin} label="Address" value={selected.address || `${selected.location || ""} ${selected.state || ""}`.trim()} />
                  <Row icon={Calendar} label="DOB" value={selected.date_of_birth} />
                  <Row label="Gender" value={selected.gender} />
                  <Row icon={Languages} label="Languages" value={selected.languages} />
                </Section>

                <Section title="About their work">
                  <Row label="Bio" value={selected.bio} multiline />
                  <Row label="Experience level" value={selected.experience_level} />
                  <Row label="Qualifications" value={selected.qualifications} multiline />
                  <Row label="Availability" value={(selected.availability_days || []).join(", ") + (selected.availability_notes ? ` · ${selected.availability_notes}` : "")} />
                  <Row label="Prof. Indemnity" value={selected.has_professional_indemnity ? "Yes" : "No"} />
                  <Row label="Public Liability" value={selected.has_public_liability ? "Yes" : "No"} />
                  <Row label="ABN" value={selected.has_abn ? (selected.abn || "Yes (not entered)") : "No"} />
                  {selected.notes && <Row label="Applicant notes" value={selected.notes} multiline />}
                </Section>

                <Section title={`Documents (${(selected.documents || []).length})`}>
                  {(selected.documents || []).length === 0 && <p className="text-xs text-slate-400">No documents submitted.</p>}
                  {(selected.documents || []).map((d) => (
                    <div key={d.key} className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-4 h-4 text-slate-500 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{d.label}</p>
                          {d.status === "have" && d.file_name && <p className="text-[11px] text-slate-500 truncate">{d.file_name}</p>}
                        </div>
                      </div>
                      {d.status === "have" && d.file_data_url ? (
                        <a href={d.file_data_url} download={d.file_name || d.key} className="text-xs text-[#87CEEB] hover:underline whitespace-nowrap" data-testid={`app-doc-download-${d.key}`}>
                          Open / download
                        </a>
                      ) : (
                        <span className="text-xs text-slate-400 whitespace-nowrap">Doesn't have yet</span>
                      )}
                    </div>
                  ))}
                </Section>

                <Section title="Admin notes">
                  <Textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    rows={3}
                    placeholder="Internal notes — visible only to admin."
                    data-testid="application-admin-notes"
                  />
                </Section>

                <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-100">
                  <p className="text-[11px] text-slate-400">ID: <span className="font-mono">{selected.application_id}</span></p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="outline" disabled={saving} onClick={() => updateStatus(selected, "reviewing")} data-testid="application-mark-reviewing">Reviewing</Button>
                    <Button size="sm" variant="outline" disabled={saving} onClick={() => updateStatus(selected, "rejected")} data-testid="application-mark-rejected">Reject</Button>
                    <Button size="sm" variant="outline" disabled={saving} onClick={() => updateStatus(selected, "archived")} data-testid="application-mark-archived">Archive</Button>
                    <Button size="sm" disabled={saving} onClick={() => updateStatus(selected, "approved")} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="application-mark-approved">
                      <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Approve
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-2">
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{title}</p>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Row({ icon: Icon, label, value, multiline }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-sm">
      <div className="w-32 flex items-center gap-1.5 text-slate-500 shrink-0">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span className="text-[11px] uppercase tracking-wider font-semibold">{label}</span>
      </div>
      <p className={`flex-1 text-slate-800 ${multiline ? "whitespace-pre-wrap" : "truncate"}`}>{value}</p>
    </div>
  );
}
